import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  CircularProgress,
  FormControl,
  FormLabel,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  coverageTypeToLabel,
  formatPolicyPeriodDisplay,
} from "@platform/btx-plan-designer";
import { matchesProposalCoverageType } from "@platform/mpx-quotes-grid";
import { Button, Dialog } from "@platform/design-system";
import { type CoverageTypeEnum } from "@platform/types";
import { useCallback, useEffect, useMemo, useState } from "react";

import { CarrierSelector } from "../selectors/CarrierSelector";
import { useAddPlanMutation } from "../hooks/useAddPlanMutation";
import {
  type NetworkOption,
  useNetworkOptions,
} from "../hooks/useNetworkOptions";
import { usePlanOptions } from "../hooks/usePlanOptions";
import { Carrier } from "../hooks/useCarriers";

interface OptionSelectorProps {
  placeholder: string;
  options: { key: string; label: string }[];
  selectedKey?: string;
  onSelect: (key: string) => void;
  disabled?: boolean;
  loading?: boolean;
}

const OptionSelector = ({
  placeholder,
  options,
  selectedKey,
  onSelect,
  disabled = false,
  loading = false,
}: OptionSelectorProps): React.JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuWidth, setMenuWidth] = useState<number>();

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const selectedLabel = options.find((o) => o.key === selectedKey)?.label;

  return (
    <>
      <Button
        variant="neutral"
        disabled={disabled || loading}
        endIcon={
          loading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <ExpandMoreIcon />
          )
        }
        onClick={(e) => {
          setMenuWidth(e.currentTarget.offsetWidth);
          setAnchorEl(e.currentTarget);
        }}
        fullWidth
        sx={{
          justifyContent: "space-between",
          textTransform: "none",
          fontSize: "0.875rem",
          fontWeight: 400,
          color: selectedLabel ? "text.primary" : "text.secondary",
        }}
      >
        {selectedLabel ?? placeholder}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { maxHeight: "40vh", width: menuWidth },
          },
        }}
      >
        {options.length === 0 ? (
          <MenuItem disabled sx={{ textTransform: "none" }}>
            No options found
          </MenuItem>
        ) : (
          options.map((option) => (
            <MenuItem
              key={option.key}
              selected={option.key === selectedKey}
              onClick={() => {
                onSelect(option.key);
                handleClose();
              }}
              sx={{ textTransform: "none", whiteSpace: "normal" }}
            >
              {option.label}
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

type AddPlanModalProps = {
  open: boolean;
  onClose: () => void;
  rfpId: string | undefined;
  brokerPortalRfpId: string | undefined;
  selectedCoverageType: CoverageTypeEnum | undefined;
  onPlanAdded: (
    coverageType: CoverageTypeEnum,
    rfpBenefitPlanGroupId: string,
  ) => void;
};

export const AddPlanModal: React.FC<AddPlanModalProps> = ({
  open,
  onClose,
  rfpId,
  brokerPortalRfpId,
  selectedCoverageType,
  onPlanAdded,
}) => {
  const [carrier, setCarrier] = useState<Carrier | null>(null);
  const [otherCarrier, setOtherCarrier] = useState<Carrier | null>(null);
  const [planOptionId, setPlanOptionId] = useState<string>();
  const [network, setNetwork] = useState<string>();

  const networkKey = useCallback(
    (option: NetworkOption): string =>
      option.networkName
        ? `${option.network}:${option.networkName}`
        : option.network,
    [],
  );

  const onClearForm = (): void => {
    setNetwork("");
    setCarrier(null);
    setOtherCarrier(null);
    setPlanOptionId("");
    onClose();
  };

  const { planOptions, isLoading: planOptionsLoading } = usePlanOptions({
    rfpId,
    enabled: open,
  });

  const { networkOptions, isLoading: networkOptionsLoading } =
    useNetworkOptions({
      rfpId,
      planOptionId,
      enabled: open,
    });

  const { addPlan, isAdding } = useAddPlanMutation({
    rfpId,
    onSuccess: () => {
      if (selectedCoverageType && planOptionId) {
        onPlanAdded(selectedCoverageType, planOptionId);
      }
      onClearForm();
    },
  });

  useEffect(() => {
    setNetwork(undefined);
  }, [planOptionId]);

  const planOptionItems = useMemo(() => {
    const alternateCounts: Record<string, number> = {};
    return planOptions
      .filter((option) =>
        selectedCoverageType
          ? matchesProposalCoverageType(
              option.coverageType,
              selectedCoverageType,
            )
          : true,
      )
      .map((option) => {
        let alternateIndex: number | undefined;
        if (option.policyPeriod === "alternate" && !option.nickname) {
          alternateCounts[option.coverageType] =
            (alternateCounts[option.coverageType] ?? 0) + 1;
          alternateIndex = alternateCounts[option.coverageType];
        }
        const periodLabel = formatPolicyPeriodDisplay(
          option.policyPeriod as Parameters<
            typeof formatPolicyPeriodDisplay
          >[0],
          option.nickname,
          alternateIndex,
        );
        const coverageLabel = selectedCoverageType
          ? undefined
          : coverageTypeToLabel(
              option.coverageType as Parameters<typeof coverageTypeToLabel>[0],
            );
        return {
          key: option.id,
          label: [coverageLabel, periodLabel, option.label]
            .filter(Boolean)
            .join(" – "),
        };
      });
  }, [planOptions, selectedCoverageType]);

  const networkOptionItems = useMemo(
    () =>
      networkOptions.map((option) => ({
        key: networkKey(option),
        label: option.label,
      })),
    [networkKey, networkOptions],
  );

  const selectedNetwork = useMemo(
    () => networkOptions.find((option) => networkKey(option) === network),
    [networkOptions, networkKey, network],
  );

  const showAllCarriers = useMemo(() => carrier?.id === "other", [carrier]);

  const selectedCarrier = showAllCarriers ? otherCarrier : carrier;

  const handleAdd = (): void => {
    if (!selectedCarrier || !planOptionId) return;
    addPlan({
      carrierId: selectedCarrier.id,
      carrierName: selectedCarrier.name,
      rfpBenefitPlanGroupId: planOptionId,
      network: selectedNetwork?.network,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isAdding ? undefined : onClearForm}
      title="Add Plan"
      maxWidth="xs"
      actions={
        <>
          <Button variant="tertiary" onClick={onClearForm} disabled={isAdding}>
            Cancel
          </Button>
          <LoadingButton
            loading={isAdding}
            disabled={!selectedCarrier || !planOptionId}
            onClick={handleAdd}
          >
            Add
          </LoadingButton>
        </>
      }
    >
      <Stack spacing={2} direction="column">
        <FormControl>
          <FormLabel sx={{ pb: 1 }}>Carrier</FormLabel>
          <Stack spacing={2}>
            <CarrierSelector
              carrier={carrier}
              rfpId={brokerPortalRfpId}
              onSelect={setCarrier}
              disabled={isAdding}
            />
            {showAllCarriers && (
              <CarrierSelector
                placeholder="Select Carrier..."
                carrier={otherCarrier}
                onSelect={setOtherCarrier}
                disabled={isAdding}
              />
            )}
          </Stack>
        </FormControl>

        <FormControl>
          <FormLabel sx={{ pb: 1 }}>Plan Options</FormLabel>
          <OptionSelector
            placeholder="Select Plan Option..."
            options={planOptionItems}
            selectedKey={planOptionId}
            onSelect={setPlanOptionId}
            disabled={isAdding}
            loading={planOptionsLoading}
          />
        </FormControl>

        <FormControl>
          <FormLabel sx={{ pb: 1 }}>Network Options</FormLabel>
          <OptionSelector
            placeholder="Select Network Option..."
            options={networkOptionItems}
            selectedKey={network}
            onSelect={setNetwork}
            disabled={!planOptionId || isAdding}
            loading={networkOptionsLoading}
          />
        </FormControl>
      </Stack>
    </Dialog>
  );
};
